import { useEffect, type FC } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { TradeBenefitsFaq } from '../TradeBenefitsFaq/TradeBenefitsFaq.tsx';
import { TradeBenefitsTestimonial } from '../TradeBenefitsTestimonial/TradeBenefitsTestimonial.tsx';
import { TradeBenefitsHero } from './TradeBenefitsHero.tsx';
import { TradeBenefitsTiles } from '../TradeBenefitsTiles.tsx';
import { TradeBenefitsCurrentTier } from './TradeBenefitsCurrentTier/TradeBenefitsCurrentTier.tsx';
import { TradeBenefitsPlanningProject } from './TradeBenefitsPlanningProject/TradeBenefitsPlanningProject.tsx';
import { TradeBenefitsYourFirm } from './TradeBenefitsYourFirm/TradeBenefitsYourFirm.tsx';

import { trackPageview, getFirmStatusTrackingParam } from '../../rewards2020/trackingHelpers.ts';
import { useServerVars } from '../../shared/ServerVarsContext.tsx';
import { getContactUrl, useTradeBenefitsContact } from '../useTradeBenefitsContact.ts';

import containerStyles from '../TradeBenefitsContainer.module.css';

import { type TradeBenefitsQuery } from './__generated__/TradeBenefitsQuery.graphql.ts';

const tradeBenefitsQuery = graphql`
    query TradeBenefitsQuery($userId: String!) {
        viewer {
            ...TradeBenefitsHero_viewer
            ...useTradeBenefitsContact_viewer
            ...TradeBenefitsCurrentTier_viewer
            ...TradeBenefitsPlanningProject_viewer
            ...TradeBenefitsFaq_viewer
            ...TradeBenefitsYourFirm_viewer
            user(userId: $userId) {
                ...trackingHelpers_user
                tradeFirm {
                    ...useTradeBenefitsContact_tradeFirm
                    ...TradeBenefitsCurrentTier_tradeFirm
                    ...TradeBenefitsPlanningProject_tradeFirm
                    rewards {
                        ...trackingHelpers_rewards
                    }
                }
            }
        }
    }
`;

const TradeBenefits: FC<{ userId: string }> = ({ userId }) => {
    const { isMobile } = useServerVars();
    const { viewer } = useLazyLoadQuery<TradeBenefitsQuery>(tradeBenefitsQuery, {
        userId,
    });

    const user = viewer?.user;
    const tradeFirm = user?.tradeFirm;
    const rewards = tradeFirm?.rewards;
    const isFirmless = !tradeFirm;

    const { isSilverPlus, isAssigned, repEmail } = useTradeBenefitsContact({
        viewer,
        tradeFirm,
    });
    const contactUrl = getContactUrl({ isAssigned, isSilverPlus, repEmail });
    const firmStatus = getFirmStatusTrackingParam({ user, rewards });

    useEffect(() => {
        trackPageview({
            pageSubType: 'program overview',
            firmStatus,
        });
    }, [firmStatus]);

    return (
        <div className={containerStyles.root}>
            <TradeBenefitsHero viewer={viewer} />
            <TradeBenefitsTiles
                isTrade
                contactUrl={contactUrl}
                isAssigned={isAssigned}
                isSilverPlus={isSilverPlus}
                isFirmless={isFirmless}
            />
            <TradeBenefitsCurrentTier
                viewer={viewer}
                tradeFirm={tradeFirm}
                isFirmless={isFirmless}
            />
            <TradeBenefitsPlanningProject
                viewer={viewer}
                tradeFirm={tradeFirm}
                isFirmless={isFirmless}
            />
            {isMobile && !isFirmless && <TradeBenefitsYourFirm viewer={viewer} />}
            <TradeBenefitsTestimonial />
            <TradeBenefitsFaq isTrade isFirmless={isFirmless} viewer={viewer} />
        </div>
    );
};

export { TradeBenefits };
